// BahnStrecke — InfraX-Jahresstrecke mit fahrendem Zug (Auftrag H Schritt 6,
// DESIGN-V3-LAGEBILD V4.2).
//
// SCAFFOLD: 1px-Doppelgleis, Monats-Ticks, Quartals-Stationen (passiert =
// gefüllt rot, "Signal passiert"), Zug fährt beim Laden zur Ist-Position,
// zurückgelegtes Gleis färbt sich synchron in Firmenrot. Der ICE-Feinbau
// (Fensterband, Drehgestelle, Alpha-Trail als Motion-Blur) macht die
// Cowork-Screenshot-Session.
//
// Datenbindung: Zug-Position = Tag im Jahr; Label = InfraX-Umsatz YTD
// (r1_reference_only: Einzeldaten sichtbar, keine Konzern-Aggregation).

import { formatEurCompact } from "../../lib/bwa-aggregate";
import { FIRMA_COLORS } from "../../lib/firma-colors";
import { clamp01, useEntrance } from "./useEntrance";

interface Props {
  ytdEur: number | null;
  /** Jahresfortschritt 0..1 (Tag im Jahr) = Zug-Position. */
  tagImJahrPct: number;
  jahr: number;
  /** Stagger-Delay (Ring → Rohre → Zug). */
  delayMs?: number;
  /** AUFTRAG M B1: Kompakt-Modus für die InfraX-Firmen-Kachel —
   *  nur Gleis + Progress + Stationen + Zug, ohne Header/Beschriftung. */
  mini?: boolean;
}

const X0 = 16;
const X1 = 584;
const Y_TRACK = 40;

const INFRAX_HEX = FIRMA_COLORS.infrax.hex;

export default function BahnStrecke({
  ytdEur,
  tagImJahrPct,
  jahr,
  delayMs = 600,
  mini = false,
}: Props) {
  const entered = useEntrance();
  const pct = clamp01(tagImJahrPct);
  const trainX = X0 + (X1 - X0) * pct;

  return (
    <div aria-label={`InfraX Jahresstrecke ${jahr}`}>
      {!mini && (
        <header className="flex items-baseline justify-between gap-2">
          <div
            className={`flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold ${FIRMA_COLORS.infrax.text}`}
          >
            <span aria-hidden className={`h-2 w-2 rounded-full ${FIRMA_COLORS.infrax.dot}`} />
            InfraX · Strecke {jahr}
          </div>
          <div className="num-mono text-[11px] text-leitstand-text">
            {ytdEur != null ? formatEurCompact(ytdEur) : "—"}
            <span className="text-leitstand-dim"> YTD · eigener Datenraum (R1)</span>
          </div>
        </header>
      )}

      <svg
        viewBox={mini ? "0 16 600 44" : "0 0 600 72"}
        width="100%"
        height={mini ? 28 : 72}
        preserveAspectRatio={mini ? "none" : "xMidYMid meet"}
        role="img"
        aria-hidden
      >
        {/* 1px-Doppelgleis. */}
        <line x1={X0} y1={Y_TRACK - 2} x2={X1} y2={Y_TRACK - 2} stroke="rgba(245,241,232,0.22)" strokeWidth={1} />
        <line x1={X0} y1={Y_TRACK + 2} x2={X1} y2={Y_TRACK + 2} stroke="rgba(245,241,232,0.22)" strokeWidth={1} />

        {/* Monats-Ticks. */}
        {Array.from({ length: 13 }).map((_, i) => {
          const x = X0 + ((X1 - X0) * i) / 12;
          return (
            <line
              key={i}
              x1={x}
              y1={Y_TRACK + 5}
              x2={x}
              y2={Y_TRACK + 9}
              stroke="rgba(245,241,232,0.18)"
              strokeWidth={0.75}
            />
          );
        })}

        {/* Zurückgelegtes Gleis färbt sich in Firmenrot (wächst mit dem Zug). */}
        <line
          x1={X0}
          y1={Y_TRACK}
          x2={X1}
          y2={Y_TRACK}
          stroke={INFRAX_HEX}
          strokeWidth={2}
          className="leitstand-anim"
          style={{
            transform: `scaleX(${entered ? pct : 0})`,
            transformOrigin: `${X0}px 0px`,
            transitionDelay: `${delayMs}ms`,
          }}
        />

        {/* Quartals-Stationen: passiert = gefüllt rot ("Signal passiert"). */}
        {[0, 1, 2, 3, 4].map((q) => {
          const x = X0 + ((X1 - X0) * q) / 4;
          const passiert = pct >= q / 4 - 0.001;
          return (
            <g key={q}>
              <circle
                cx={x}
                cy={Y_TRACK}
                r={3}
                fill={passiert ? INFRAX_HEX : "#0b0b0c"}
                stroke={passiert ? INFRAX_HEX : "rgba(245,241,232,0.35)"}
                strokeWidth={1}
              />
              {!mini && (
                <text
                  x={x}
                  y={Y_TRACK + 20}
                  textAnchor="middle"
                  fontSize="7"
                  fill="#a8a29a"
                  fontFamily="JetBrains Mono, ui-monospace, monospace"
                >
                  {q === 0 ? "JAN" : q === 4 ? "DEZ" : `Q${q}`}
                </text>
              )}
            </g>
          );
        })}

        {/* Zug: schlanke Kapsel mit runder Nase, fährt zur Ist-Position.
            Alpha-Trail (3 abnehmende Segmente) als Motion-Blur-Andeutung. */}
        <g
          className="leitstand-anim"
          style={{
            transform: `translateX(${entered ? trainX - X0 : 0}px)`,
            transitionDelay: `${delayMs}ms`,
          }}
        >
          {[18, 12, 6].map((off, i) => (
            <rect
              key={off}
              x={X0 - 14 - off}
              y={Y_TRACK - 8}
              width={10}
              height={6}
              rx={3}
              fill={INFRAX_HEX}
              opacity={0.12 * (3 - i)}
            />
          ))}
          <path
            d={`M ${X0 - 16} ${Y_TRACK - 9}
                h 20
                q 8 0 10 4.5
                q -2 4.5 -10 4.5
                h -20
                z`}
            fill={INFRAX_HEX}
          />
          {/* Fensterband-Hairline + 2 Segment-Trennungen (Andeutung). */}
          <line x1={X0 - 13} y1={Y_TRACK - 4.5} x2={X0 + 8} y2={Y_TRACK - 4.5} stroke="#0b0b0c" strokeWidth={1} opacity={0.6} />
          <line x1={X0 - 8} y1={Y_TRACK - 9} x2={X0 - 8} y2={Y_TRACK} stroke="#0b0b0c" strokeWidth={0.75} opacity={0.5} />
          <line x1={X0 - 1} y1={Y_TRACK - 9} x2={X0 - 1} y2={Y_TRACK} stroke="#0b0b0c" strokeWidth={0.75} opacity={0.5} />
        </g>
      </svg>
    </div>
  );
}
